// pages/CreditEnginePage.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  TextInput,
  Alert,
  Badge,
  Spinner,
  Modal,
  Select,
} from "flowbite-react";
import {
  HiOutlineSparkles,
  HiOutlineShieldCheck,
  HiOutlineCog,
  HiOutlineInformationCircle,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineEye,
  HiPencil,
  HiTrash,
  HiThumbDown,
  HiThumbUp,
  HiSearch,
} from "react-icons/hi";
import axiosInstance from "../config/axiosInstance";
import { useApplication } from "../contexts/ApplicationContext";
import { isValidCNPJ, isValidCPF } from "../utils/utils";
import { ADMIN, QUERIES } from "../config/apiConfig";
import "./CreditEnginePage.css";

const cx = (...c) => c.filter(Boolean).join(" ");

// ======================
// Helpers gerais
// ======================
const onlyDigits = (v = "") => v.replace(/\D/g, "");

// 000.000.000-00 ou 00.000.000/0000-00
function formatCpfCnpj(v = "") {
  const digits = onlyDigits(v);

  if (digits.length <= 11) {
    // CPF
    return digits
      .replace(/^(\d{3})(\d)/, "$1.$2")
      .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3-$4")
      .slice(0, 14);
  }

  // CNPJ
  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3/$4")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, "$1.$2.$3/$4-$5")
    .slice(0, 18);
}

function validateCpfCnpj(value) {
  const digits = onlyDigits(value);
  if (!digits) return { ok: false, message: "Informe um CPF ou CNPJ." };

  if (digits.length === 11 && isValidCPF(digits)) {
    return { ok: true, type: "CPF" };
  }

  if (digits.length === 14 && isValidCNPJ(digits)) {
    return { ok: true, type: "CNPJ" };
  }

  return { ok: false, message: "CPF/CNPJ inválido. Verifique os dados." };
}

const moneyFmt = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});
const formatMoney = (v) => {
  const num = Number(v);
  if (!num) return null;
  return moneyFmt.format(num);
};

const TEMPLATE_STORAGE_KEY = "creditEngineSelectedTemplate";

const parseNumber = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number") return value;
  const normalized = String(value)
    .replace(/[R$\s.]/g, "")
    .replace(",", ".");
  const n = Number(normalized);
  return Number.isNaN(n) ? null : n;
};

const PRESET_RECENCY_LABELS = {
  veryRecent: "Muito recente (<1 mês)",
  recent: "Recente (1-3 meses)",
  moderate: "Intermediário (3-6 meses)",
  old: "Antigo (>6 meses)",
};

const describeRecency = (value) =>
  value ? PRESET_RECENCY_LABELS[value] || value : "Sem recência";

const formatDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("pt-BR");
};

const getMonthsSinceDate = (dateValue) => {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  let years = now.getFullYear() - date.getFullYear();
  let months = now.getMonth() - date.getMonth();
  let days = now.getDate() - date.getDate();

  let totalMonths = years * 12 + months;
  if (days < 0) totalMonths -= 1;
  return totalMonths >= 0 ? String(totalMonths) : "";
};

const strongNumber = (value) => (
  <strong className="cep-config-strong">{value}</strong>
);

// ======================
// Helpers visuais de risco
// ======================
const RISK_CONFIG = {
  BAIXO: {
    label: "Risco Baixo",
    badgeClass: "cep-risk-badge cep-risk-badge-low",
  },
  MÉDIO: {
    label: "Risco Médio",
    badgeClass: "cep-risk-badge cep-risk-badge-medium",
  },
  ALTO: {
    label: "Risco Alto",
    badgeClass: "cep-risk-badge cep-risk-badge-high",
  },
  ALTÍSSIMO: {
    label: "Risco Altíssimo",
    badgeClass: "cep-risk-badge cep-risk-badge-very-high",
  },
};

// mapeia código de regra -> label legível
const RULE_LABELS = {
  PROTESTO_VALOR_ACIMA_MINIMO: "Protestos acima do valor mínimo configurado",
  CHEQUES_SEM_FUNDO_EXISTENTE: "Existência de cheques sem fundo",
  PENDENCIAS_ACIMA_MINIMO: "Pendências acima do valor mínimo configurado",
  SCORE_ABAIXO_THRESHOLD: "Score abaixo do limiar configurado",
  FUNDACAO_POSTERIOR_MINIMA: "Fundação posterior à data mínima configurada",
};

// ======================
// Paleta para o gauge de score por classificação
// ======================
const SCORE_COLOR = {
  BAIXO: "#10b981", // emerald
  MÉDIO: "#f59e0b", // amber
  ALTO: "#f97316", // orange
  ALTÍSSIMO: "#ef4444", // red
};

// ======================
// Skeleton para loading da análise
// ======================
function SkeletonLine({ width = "100%", height = 10, className }) {
  return (
    <div
      className={cx("cep-skeleton-line", className)}
      style={{ width, height }}
    />
  );
}

function AnalysisResultSkeleton() {
  return (
    <div className="cep-analysis-card cep-analysis-card--skeleton">
      <div className="cep-analysis-header">
        <div className="cep-analysis-header-icon cep-analysis-header-icon--pulse" />
        <SkeletonLine width="160px" height={16} />
      </div>

      <div className="cep-analysis-main">
        <div className="cep-analysis-main-left">
          <div className="cep-gauge-wrapper cep-gauge-wrapper--skeleton">
            <div className="cep-gauge-skeleton-outer" />
            <div className="cep-gauge-skeleton-inner" />
          </div>
          <div className="cep-analysis-main-text">
            <SkeletonLine width="130px" height={14} />
            <SkeletonLine width="220px" />
            <div className="cep-chip-row">
              <SkeletonLine width="110px" height={20} />
              <SkeletonLine width="160px" height={20} />
            </div>
          </div>
        </div>
        <div className="cep-analysis-main-right">
          <SkeletonLine width="70%" height={14} />
          <SkeletonLine width="100%" />
          <SkeletonLine width="80%" />
        </div>
      </div>

      <div className="cep-analysis-footer">
        <div className="cep-analysis-col cep-analysis-col--positive">
          <SkeletonLine width="60%" />
          <SkeletonLine width="100%" />
          <SkeletonLine width="80%" />
        </div>
        <div className="cep-analysis-col cep-analysis-col--negative">
          <SkeletonLine width="60%" />
          <SkeletonLine width="100%" />
          <SkeletonLine width="75%" />
        </div>
      </div>
      <div className="cep-analysis-col cep-analysis-col--rules">
        <SkeletonLine width="70%" />
        <div className="cep-rule-chips-skeleton">
          <SkeletonLine width="40%" height={18} />
          <SkeletonLine width="50%" height={18} />
          <SkeletonLine width="30%" height={18} />
        </div>
      </div>
    </div>
  );
}

function ConfigCardSkeleton() {
  return (
    <div className="cep-config-card cep-config-card--skeleton">
      <div className="cep-config-skeleton-header">
        <div className="cep-config-skeleton-title">
          <SkeletonLine width="210px" height={12} />
          <SkeletonLine width="160px" height={10} />
        </div>
        <SkeletonLine width="90px" height={20} className="cep-skeleton-pill" />
      </div>
      <div className="cep-config-skeleton-grid">
        {Array.from({ length: 4 }).map((_, idx) => (
          <div key={idx} className="cep-config-skeleton-item">
            <div className="cep-config-skeleton-item-header">
              <SkeletonLine width="90px" height={10} />
            </div>
            <SkeletonLine width="100%" height={10} />
            <SkeletonLine width="80%" height={10} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ======================
// Card de resultado da análise
// ======================
function AnalysisResultCard({ result, onOpenDetails }) {
  if (!result) return null;

  const {
    statusRetorno,
    scoreFinal,
    classificacaoRisco,
    autoReprovadoPeloMotor,
    regrasMotorAcionadas = [],
    observacoesPositivas = [],
    observacoesNegativas = [],
    recomendacao,
    providerId,
    prioridade,
  } = result;

  if (statusRetorno != 1) {
    return (
      <div className="cep-error-block">
        <div className="cep-error-title">
          <HiOutlineXCircle className="cep-error-icon" />
          <span>Não foi possível obter o resultado do motor.</span>
        </div>
        <p>Verifique a configuração do motor ou tente novamente.</p>
      </div>
    );
  }

  const inferTypeFromText = (text = "") => {
    const t = text.toLowerCase();

    if (t.includes("protesto")) return "PROTESTOS";
    if (t.includes("pendênc") || t.includes("pendenc")) return "PENDENCIAS";
    if (t.includes("cheque")) return "CHEQUES";
    if (t.includes("score")) return "SCORE";

    return "GENERIC";
  };

  const riskCfg = RISK_CONFIG[classificacaoRisco] || RISK_CONFIG["MÉDIO"] || {};

  const isNegar = recomendacao === "NEGAR";
  const isAprovarComRestricoes = recomendacao === "APROVAR_COM_RESTRICOES";
  const isAprovar = recomendacao === "APROVAR";

  const recomendacaoLabel =
    recomendacao === "NEGAR"
      ? "Negar crédito"
      : recomendacao === "APROVAR_COM_RESTRICOES"
      ? "Aprovar com restrições"
      : recomendacao === "APROVAR"
      ? "Aprovar crédito"
      : recomendacao || "Análise não concluída";

  const recomendacaoClass = isNegar
    ? "cep-recommendation--deny"
    : isAprovarComRestricoes
    ? "cep-recommendation--restrict"
    : isAprovar
    ? "cep-recommendation--approve"
    : "cep-recommendation--neutral";

  const scoreValue =
    typeof scoreFinal === "number"
      ? Math.max(0, Math.min(1000, scoreFinal))
      : null;
  const scorePercent = scoreValue != null ? (scoreValue / 1000) * 100 : 0;
  const gaugeColor =
    SCORE_COLOR[classificacaoRisco] || SCORE_COLOR["MÉDIO"] || "#6366f1";

  // pequeno mapa de ícones por regra
  const RULE_ICON_MAP = {
    PROTESTO_VALOR_ACIMA_MINIMO: HiOutlineInformationCircle,
    CHEQUES_SEM_FUNDO_EXISTENTE: HiOutlineXCircle,
    PENDENCIAS_ACIMA_MINIMO: HiOutlineXCircle,
    SCORE_ABAIXO_THRESHOLD: HiOutlineShieldCheck,
    FUNDACAO_POSTERIOR_MINIMA: HiOutlineXCircle,
  };

  return (
    <div className="cep-analysis-card">
      {/* título do bloco */}
      <div className="cep-analysis-header">
        <div className="cep-analysis-header-icon">
          <HiOutlineShieldCheck className="cep-analysis-header-icon-svg" />
        </div>
        <div className="cep-analysis-header-text">
          <p className="cep-analysis-header-title">
            Parecer de crédito do motor
          </p>
          <p className="cep-analysis-header-subtitle">
            Resultado automático com base nas regras configuradas e nas
            informações de crédito do cliente.
          </p>
        </div>
      </div>

      {/* header score + risco + meta + recomendação */}
      <div className="cep-analysis-main">
        {/* recomendação final em destaque */}
        <div className={cx("cep-analysis-main-right", recomendacaoClass)}>
          <div className="cep-recommendation-header">
            {isNegar && (
              <div className="cep-recommendation-icon-wrapper">
                <HiThumbDown className="cep-recommendation-icon" />
              </div>
            )}
            {isAprovar && (
              <div className="cep-recommendation-icon-wrapper">
                <HiThumbUp className="cep-recommendation-icon" />
              </div>
            )}
            {isAprovarComRestricoes && (
              <div className="cep-recommendation-icon-wrapper">
                <HiOutlineInformationCircle className="cep-recommendation-icon" />
              </div>
            )}
            {!isNegar && !isAprovar && !isAprovarComRestricoes && (
              <div className="cep-recommendation-icon-wrapper">
                <HiOutlineInformationCircle className="cep-recommendation-icon" />
              </div>
            )}

            <div className="cep-recommendation-text">
              <span className="cep-recommendation-label">
                Recomendação do motor
              </span>
              <span className="cep-recommendation-title">
                {recomendacaoLabel}
              </span>
            </div>
          </div>

          <p className="cep-recommendation-description">
            Utilize este parecer como referência principal na decisão de
            crédito, combinado com a política interna e o julgamento do
            analista.
          </p>
        </div>
        {/* score + risco + meta */}
        <div className="cep-analysis-main-left">
          <div className="cep-analysis-main-text">
            <div className="cep-chip-row">
              {/* chip risco */}
              <span className={cx(riskCfg.badgeClass)}>
                <span className="cep-chip-dot" />
                {riskCfg.label || "Risco não classificado"}
              </span>

              {/* status auto reprovação */}
              {autoReprovadoPeloMotor ? (
                <span className="cep-chip cep-chip--danger">
                  <HiOutlineXCircle className="cep-chip-icon" />
                  Reprovado automaticamente pelo motor
                </span>
              ) : (
                <span className="cep-chip cep-chip--success">
                  <HiOutlineCheckCircle className="cep-chip-icon" />
                  Sem regra de auto reprovação acionada
                </span>
              )}
            </div>

            <p className="cep-analysis-description">
              Resultado consolidado do motor de crédito, considerando as regras
              configuradas (protestos, pendências, cheques, score e tempo de
              empresa) e o histórico da empresa consultada.
            </p>
          </div>
        </div>
      </div>

      {/* observações e regras acionadas */}
      <div className="cep-analysis-footer grid-cols-1 md:grid-cols-2">
        {/* positivas */}
        <div className="cep-analysis-col cep-analysis-col--positive">
          <div className="cep-analysis-col-header">
            <HiOutlineCheckCircle className="cep-analysis-col-icon" />
            <p className="cep-analysis-col-title">Observações positivas</p>
          </div>
          {observacoesPositivas?.length ? (
            <ul className="cep-observations-list cep-observations-list--positive">
              {observacoesPositivas.map((obs, idx) => (
                <li key={idx} className="cep-observations-item">
                  <span className="cep-observations-dot cep-observations-dot--positive" />
                  <span>{obs}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="cep-observations-empty cep-observations-empty--positive">
              Nenhum ponto positivo relevante identificado.
            </p>
          )}
        </div>

        {/* negativas */}
        <div className="cep-analysis-col cep-analysis-col--negative">
          <div className="cep-analysis-col-header">
            <HiOutlineXCircle className="cep-analysis-col-icon" />
            <p className="cep-analysis-col-title">Observações negativas</p>
          </div>
          {observacoesNegativas?.length ? (
            <ul className="cep-observations-list cep-observations-list--negative">
              {observacoesNegativas.map((obs, idx) => {
                const text =
                  typeof obs === "string"
                    ? obs
                    : obs?.label || obs?.descricao || obs?.texto || "";

                const type =
                  typeof obs === "object" && obs?.type
                    ? obs.type
                    : inferTypeFromText(text);

                return (
                  <li key={idx} className="cep-observations-item">
                    <span className="cep-observations-dot cep-observations-dot--negative" />
                    <span className="cep-observations-main">
                      <span>{text}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="cep-observations-empty cep-observations-empty--negative">
              Nenhum fator de risco relevante informado.
            </p>
          )}
        </div>
      </div>

      <div className="cep-analysis-col cep-analysis-col--rules">
        <div className="cep-analysis-col-header">
          <HiOutlineInformationCircle className="cep-analysis-col-icon" />
          <p className="cep-analysis-col-title">Regras do motor acionadas</p>
        </div>

        {regrasMotorAcionadas?.length ? (
          <>
            <p className="cep-rules-summary">
              <HiOutlineSparkles className="cep-rules-summary-icon" />
              <span>
                {regrasMotorAcionadas.length} regra
                {regrasMotorAcionadas.length > 1 ? "s" : ""} do motor foram
                disparadas para este caso.
              </span>
            </p>
            <div className="cep-rule-chips">
              {regrasMotorAcionadas.map((rule, idx) => {
                const Icon = RULE_ICON_MAP[rule] || HiOutlineInformationCircle;
                return (
                  <span key={`${rule}-${idx}`} className="cep-rule-chip">
                    <Icon className="cep-rule-chip-icon" />
                    <span className="cep-rule-chip-dot" />
                    {RULE_LABELS[rule] || rule}
                  </span>
                );
              })}
            </div>
          </>
        ) : (
          <p className="cep-observations-empty cep-observations-empty--rules">
            Nenhuma regra de auto reprovação foi disparada.
          </p>
        )}
      </div>
    </div>
  );
}

// ======================
// Componente principal
// ======================
export default function CreditEnginePage() {
  const [cpfCnpj, setCpfCnpj] = useState("");
  const [cpfCnpjError, setCpfCnpjError] = useState(null);

  const [loadingConfig, setLoadingConfig] = useState(false);
  const [config, setConfig] = useState(null);
  const [baseConfig, setBaseConfig] = useState(null);
  const [alert, setAlert] = useState(null);
  const navigate = useNavigate();
  const isMountedRef = useRef(false);

  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [presets, setPresets] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState("");
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [presetPreview, setPresetPreview] = useState({
    open: false,
    preset: null,
  });
  const [presetDelete, setPresetDelete] = useState(null);

  const { userPermission } = useApplication();
  const canEdit = ["ADMIN", "POWER_USER"].includes(userPermission);

  const [detailsModal, setDetailsModal] = useState({
    open: false,
    type: null,
    label: "",
    observation: null,
  });

  const [baseQuery, setBaseQuery] = useState(null);
  const [baseQueryLoading, setBaseQueryLoading] = useState(false);

  const hasAnyTemplate = presets.length > 0;
  const canRunSimulation = hasAnyTemplate;
  const selectedPreset = selectedPresetId
    ? presets.find((item) => String(item.id) === String(selectedPresetId))
    : null;

  const loadConfig = useCallback(async () => {
    setLoadingConfig(true);
    try {
      const { data } = await axiosInstance.get(ADMIN.GET_ENGINE_CREDIT);
      if (!isMountedRef.current) return;
      const loaded = data?.data || data || null;
      setConfig(loaded);
      setBaseConfig(loaded);
    } catch (err) {
      console.error(err);
      if (isMountedRef.current) {
        setAlert({
          type: "error",
          message:
            "Não foi possível carregar as configurações do motor de crédito.",
        });
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingConfig(false);
      }
    }
  }, []);

  const mergeConfigWithPreset = useCallback((base, presetConfig) => {
    if (!presetConfig) return base;
    return {
      ...base,
      ...presetConfig,
      advanced: {
        ...(base?.advanced || {}),
        ...(presetConfig?.advanced || {}),
      },
      legal: {
        ...(base?.legal || {}),
        ...(presetConfig?.legal || {}),
      },
      recency: {
        ...(base?.recency || {}),
        ...(presetConfig?.recency || {}),
      },
    };
  }, []);

  useEffect(() => {
    if (!baseConfig) return;
    if (!selectedPresetId) {
      setConfig(baseConfig);
      return;
    }
    const preset = presets.find(
      (item) => String(item.id) === String(selectedPresetId)
    );
    if (preset) {
      const nextConfig = preset?.config
        ? mergeConfigWithPreset(baseConfig, preset.config)
        : { ...baseConfig };
      const rawQueryId =
        preset?.queryId != null ? preset.queryId : preset?.config?.queryId;
      const normalizedQueryId = Number(rawQueryId);
      if (Number.isFinite(normalizedQueryId) && normalizedQueryId > 0) {
        nextConfig.queryId = normalizedQueryId;
      }
      setConfig(nextConfig);
    }
  }, [baseConfig, presets, selectedPresetId, mergeConfigWithPreset]);

  const loadPresets = useCallback(async () => {
    if (!canEdit) return;
    setPresetsLoading(true);
    try {
      const { data } = await axiosInstance.get(ADMIN.GET_CREDIT_ENGINE_PRESETS);
      const items = data?.data || data || [];
      if (!isMountedRef.current) return;
      if (Array.isArray(items)) {
        setPresets(items);
      } else {
        setPresets([]);
      }
    } catch (err) {
      console.error(err);
      if (isMountedRef.current) {
        setPresets([]);
      }
    } finally {
      if (isMountedRef.current) {
        setPresetsLoading(false);
      }
    }
  }, [canEdit]);

  useEffect(() => {
    isMountedRef.current = true;
    const storedTemplate = localStorage.getItem(TEMPLATE_STORAGE_KEY);
    if (storedTemplate) {
      setSelectedPresetId(storedTemplate);
    }
    loadConfig();
    loadPresets();
    const refreshListener = () => {
      if (isMountedRef.current) {
        loadConfig();
      }
    };
    window.addEventListener("credit-engine-config-updated", refreshListener);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener(
        "credit-engine-config-updated",
        refreshListener
      );
    };
  }, [loadConfig, loadPresets]);

  useEffect(() => {
    if (presetsLoading) return;
    if (!selectedPresetId) return;
    const exists = presets.some(
      (item) => String(item.id) === String(selectedPresetId)
    );
    if (!exists) {
      setSelectedPresetId("");
      localStorage.setItem(TEMPLATE_STORAGE_KEY, "");
    }
  }, [presets, presetsLoading, selectedPresetId]);

  useEffect(() => {
    const queryId = config?.queryId;
    if (!queryId) {
      setBaseQuery(null);
      return;
    }

    let mounted = true;
    setBaseQueryLoading(true);
    axiosInstance
      .get(`${QUERIES.GET_QUERY}/${queryId}`)
      .then(({ data }) => {
        if (!mounted) return;
        setBaseQuery(data?.data || data || null);
      })
      .catch((error) => {
        console.error(error);
        if (mounted) setBaseQuery(null);
      })
      .finally(() => {
        if (mounted) setBaseQueryLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [config?.queryId]);

  const handleOpenSettings = () => {
    if (!canEdit) {
      setAlert({
        type: "warning",
        message: "Você não tem permissão para editar o motor de crédito.",
      });
      return;
    }
    navigate("/credit-engine/settings");
  };

  const handleSelectPreset = (id) => {
    setSelectedPresetId(id);
    localStorage.setItem(TEMPLATE_STORAGE_KEY, id || "");
    setShowTemplatePicker(false);
  };

  const handlePreviewPreset = (preset) => {
    if (!preset) return;
    setSelectedPresetId(String(preset.id));
    setPresetPreview({ open: true, preset });
  };

  const handleEditPreset = (preset) => {
    if (!preset) return;
    navigate("/credit-engine/settings", { state: { preset } });
  };

  const handleRequestDeletePreset = (preset) => {
    setPresetDelete(preset);
  };

  const handleConfirmDeletePreset = async () => {
    if (!presetDelete) return;
    try {
      await axiosInstance.delete(
        ADMIN.DELETE_CREDIT_ENGINE_PRESET(presetDelete.id)
      );
      setPresets((prev) => prev.filter((item) => item.id !== presetDelete.id));
      if (String(selectedPresetId) === String(presetDelete.id)) {
        setSelectedPresetId("active");
      }
      setAlert({
        type: "success",
        message: "Template removido com sucesso.",
      });
    } catch (error) {
      console.error(error);
      setAlert({
        type: "error",
        message: "Erro ao remover o template. Tente novamente.",
      });
    } finally {
      setPresetDelete(null);
    }
  };

  const renderPresetSummary = () => {
    const presetConfig = presetPreview.preset?.config || {};
    const recency = presetConfig.recency || {};
    const advanced = presetConfig.advanced || {};
    const legal = presetConfig.legal || {};

    const autoItems = [];
    if (presetConfig.autoRejectProtesto) {
      const minValue = parseNumber(presetConfig.protestoMinValue);
      autoItems.push({
        label: minValue
          ? `Protestos acima de ${formatMoney(minValue)}`
          : "Qualquer protesto",
        meta: describeRecency(recency.protesto),
      });
    }
    if (presetConfig.autoRejectChequeSemFundo) {
      const minCount = presetConfig.chequeMinCount;
      autoItems.push({
        label: minCount
          ? `Cheques sem fundo a partir de ${minCount}`
          : "Qualquer cheque sem fundo",
        meta: describeRecency(recency.chequeSemFundo),
      });
    }
    if (presetConfig.autoRejectPendenciaFinanceira) {
      const minValue = parseNumber(presetConfig.pendenciaMinValue);
      autoItems.push({
        label: minValue
          ? `Pendências acima de ${formatMoney(minValue)}`
          : "Qualquer pendência financeira",
        meta: describeRecency(recency.pendenciaFinanceira),
      });
    }

    const mainItems = [];
    if (presetConfig.scoreThreshold != null) {
      mainItems.push({
        label: `Score mínimo ${presetConfig.scoreThreshold}`,
        meta: "Regra principal",
      });
    }
    if (presetConfig.foundationMinDate) {
      const months = getMonthsSinceDate(presetConfig.foundationMinDate);
      mainItems.push({
        label: `Fundação mínima: ${months || "-"} meses`,
        meta: formatDate(presetConfig.foundationMinDate) || "Sem data",
      });
    }

    const advancedItems = [];
    if (advanced.behavioral?.active && advanced.behavioral?.minScore != null) {
      advancedItems.push({
        label: `Score comportamental mínimo ${advanced.behavioral.minScore}`,
        meta: "Comportamento",
      });
    }
    if (advanced.location?.active && advanced.location?.states) {
      const mode =
        advanced.location.mode === "allowed" ? "Permitir" : "Bloquear";
      advancedItems.push({
        label: `${mode} UF(s): ${advanced.location.states}`,
        meta: "Localização",
      });
    }
    if (advanced.companySize?.active && advanced.companySize?.porte) {
      advancedItems.push({
        label: `Porte mínimo: ${advanced.companySize.porte}`,
        meta: "Porte",
      });
    }
    if (advanced.creditLimit?.active) {
      const minValue = parseNumber(advanced.creditLimit.minValue);
      advancedItems.push({
        label: minValue
          ? `Limite mínimo ${formatMoney(minValue)}`
          : "Limite mínimo não definido",
        meta: "Limite de crédito",
      });
    }
    if (advanced.faturamento?.active) {
      const minValue = parseNumber(advanced.faturamento.minValue);
      advancedItems.push({
        label: minValue
          ? `Faturamento mínimo ${formatMoney(minValue)}`
          : "Faturamento mínimo não definido",
        meta: "Faturamento",
      });
    }
    if (advanced.age?.active) {
      const minAge = advanced.age.minAge || "0";
      const maxAge = advanced.age.maxAge || "∞";
      advancedItems.push({
        label: `Faixa de idade: ${minAge} a ${maxAge} anos`,
        meta: "Idade",
      });
    }

    const legalItems = [];
    if (legal.acaoCivil) {
      legalItems.push({
        label: "Ação cível",
        meta: describeRecency(recency.acaoCivil),
      });
    }
    if (legal.recuperacaoJudicial) {
      legalItems.push({
        label: "Recuperação judicial",
        meta: describeRecency(recency.recuperacaoJudicial),
      });
    }
    if (legal.processosLesivos) {
      legalItems.push({
        label: legal.processosImageUrl
          ? `Processos lesivos (link disponível)`
          : "Processos lesivos",
        meta: describeRecency(recency.processosLesivos),
      });
    }

    const renderSection = (title, items, emptyText) => (
      <div className="cep-preset-section">
        <h4 className="cep-preset-section-title">{title}</h4>
        {items.length === 0 ? (
          <p className="cep-preset-empty">{emptyText}</p>
        ) : (
          <div className="cep-preset-items">
            {items.map((item, idx) => (
              <div key={`${title}-${idx}`} className="cep-preset-item">
                <span className="cep-preset-item-label">{item.label}</span>
                <span className="cep-preset-item-meta">{item.meta}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );

    return (
      <div className="cep-preset-summary">
        {renderSection(
          "Reprovação automática",
          autoItems,
          "Nenhuma regra automática configurada."
        )}
        {renderSection(
          "Regras principais",
          mainItems,
          "Sem regras principais definidas."
        )}
        {renderSection(
          "Filtros avançados",
          advancedItems,
          "Nenhum filtro avançado ativo."
        )}
        {renderSection(
          "Monitoramento legal",
          legalItems,
          "Sem regras legais configuradas."
        )}
        {presetConfig.notes ? (
          <div className="cep-preset-section">
            <h4 className="cep-preset-section-title">Observações internas</h4>
            <p className="cep-preset-notes">{presetConfig.notes}</p>
          </div>
        ) : null}
      </div>
    );
  };

  const handleCpfCnpjChange = (e) => {
    const digits = onlyDigits(e.target.value).slice(0, 14);
    setCpfCnpj(digits);
    if (cpfCnpjError) setCpfCnpjError(null);
  };

  const handleSimulate = async (e) => {
    e.preventDefault();
    const result = validateCpfCnpj(cpfCnpj);

    if (!result.ok) {
      setCpfCnpjError(result.message);
      return;
    }

    if (baseQueryLoading || !baseQuery?.url) {
      setAlert({
        type: "warning",
        message:
          "Defina uma consulta base válida para o motor antes de simular.",
      });
      return;
    }

    try {
      setAnalysisLoading(true);
      setAlert(null);
      setAnalysisResult(null);

      const presetQuery = selectedPresetId
        ? `&presetId=${selectedPresetId}`
        : "";

      const resp = await axiosInstance.get(
        `${QUERIES.GET_V2_QUERY}/${baseQuery.url}/${cpfCnpj}?isCreditEngine=1${presetQuery}`
      );

      // tenta achar o objeto de crédito preditivo em níveis diferentes
      const payload =
        resp?.data?.creditoPreditivo ||
        resp?.data?.data?.creditoPreditivo ||
        resp?.data?.data ||
        resp?.data;

      setAnalysisResult(payload);

      setAlert({
        type: "success",
        message: `Análise concluída para este ${result.type}. Veja o resumo ao lado.`,
      });
    } catch (err) {
      console.error(err);
      setAlert({
        type: "error",
        message:
          "Ocorreu um erro ao executar a análise. Verifique a configuração do motor ou tente novamente.",
      });
    } finally {
      setAnalysisLoading(false);
    }
  };

  // permissao de consulta nao bloqueia o acesso a esta tela

  // Considerar "configurada" se estiver ativa E tiver pelo menos uma regra/threshold preenchida
  const engineConfigured =
    config &&
    config.isActive &&
    (config.autoRejectProtesto ||
      config.autoRejectChequeSemFundo ||
      config.autoRejectPendenciaFinanceira ||
      config.scoreThreshold ||
      config.protestoMinValue ||
      config.chequeMinCount ||
      config.pendenciaMinValue ||
      config.foundationMinDate);

  const appliedRules = [];
  if (config?.autoRejectProtesto) {
    const protestoMinValue = formatMoney(config.protestoMinValue);
    appliedRules.push({
      id: "protestos",
      title: "Protestos",
      description: protestoMinValue ? (
        <>
          Reprova automaticamente se o valor total de protestos for igual ou
          superior a {strongNumber(protestoMinValue)}.
        </>
      ) : (
        "Reprova automaticamente se houver qualquer protesto."
      ),
    });
  }
  if (config?.autoRejectChequeSemFundo) {
    const chequeMinCount =
      config.chequeMinCount && Number(config.chequeMinCount) > 0
        ? config.chequeMinCount
        : null;
    appliedRules.push({
      id: "cheques",
      title: "Cheques sem fundo",
      description: chequeMinCount ? (
        <>
          Reprova automaticamente a partir de {strongNumber(chequeMinCount)}{" "}
          cheque(s) sem fundo.
        </>
      ) : (
        "Reprova automaticamente se existir qualquer cheque sem fundo."
      ),
    });
  }
  if (config?.autoRejectPendenciaFinanceira) {
    const pendenciaMinValue = formatMoney(config.pendenciaMinValue);
    appliedRules.push({
      id: "pendencias",
      title: "Pendências financeiras",
      description: pendenciaMinValue ? (
        <>
          Reprova automaticamente se o valor total de pendências for igual ou
          superior a {strongNumber(pendenciaMinValue)}.
        </>
      ) : (
        "Reprova automaticamente se existirem pendências em aberto."
      ),
    });
  }
  if (config?.scoreThreshold) {
    appliedRules.push({
      id: "score",
      title: "Score mínimo",
      description: (
        <>
          Reprova automaticamente se o score for menor que{" "}
          {strongNumber(config.scoreThreshold)}.
        </>
      ),
    });
  }
  if (config?.foundationMinDate) {
    const foundationAgeMonths = getMonthsSinceDate(config.foundationMinDate);
    const foundationDateLabel = formatDate(config.foundationMinDate);
    appliedRules.push({
      id: "fundacao",
      title: "Data mínima de fundação",
      description: foundationAgeMonths ? (
        <>
          Reprova automaticamente empresas com menos de{" "}
          {strongNumber(foundationAgeMonths)} meses de fundação.
        </>
      ) : (
        <>
          Reprova automaticamente empresas com data de fundação posterior a{" "}
          {strongNumber(foundationDateLabel)}.
        </>
      ),
    });
  }

  const handleOpenDetails = (ctx) => {
    setDetailsModal({
      open: true,
      type: ctx?.type || null,
      label: ctx?.label || "Detalhes da análise",
      observation: ctx?.observation || null,
    });
  };

  const handleCloseDetails = () => {
    setDetailsModal((prev) => ({ ...prev, open: false }));
  };

  return (
    <>
      <div className="credit-engine-page">
        <div className="cep-shell shadow-md">
          {/* Header */}
          <header className="cep-header">
            <div className="cep-header-top-row">
              <div className="cep-header-main-row">
                <div className="cep-header-main-text">
                  <div className="cep-header-icon-wrapper">
                    <HiOutlineShieldCheck className="cep-header-main-icon" />
                  </div>
                  <div>
                    <h1 className="cep-header-title">Motor de Crédito</h1>
                    <p className="cep-header-subtitle">
                      Centralize as regras de aprovação e reprovação das suas
                      análises de crédito.
                    </p>
                  </div>
                </div>

                <div className="cep-header-meta">
                  {!canEdit && (
                    <span className="cep-header-permission">
                      Apenas ADMIN ou POWER_USER podem editar.
                    </span>
                  )}
                </div>
              </div>
              <div className="cep-header-actions">
                <Button
                  size="sm"
                  onClick={handleOpenSettings}
                  className="cep-settings-button"
                >
                  <div className="flex items-center gap-2">
                    <HiOutlineCog className="cep-settings-button-icon" />
                    <span>Criar novo motor</span>
                  </div>
                </Button>
              </div>
            </div>
          </header>

          {/* Bloco principal */}
          <section className="cep-main-card">
            <div className="cep-layout-stack">
              <div className="cep-template-block">
                <div className="cep-section-header">
                  <div>
                    <h2 className="cep-section-title">Templates do motor</h2>
                    <p className="cep-section-subtitle">
                      Escolha um template para aplicar durante a simulação.
                    </p>
                  </div>
                  <Button
                    size="xs"
                    color="light"
                    onClick={() => setShowTemplatePicker((prev) => !prev)}
                    className="cep-template-toggle"
                  >
                    {showTemplatePicker ? "Fechar lista" : "Trocar template"}
                  </Button>
                </div>
                <div className="cep-template-list">
                  {selectedPreset ? (
                    <div className="cep-template-card cep-template-card--active">
                      <div className="cep-template-main">
                        <span className="cep-template-title">
                          {selectedPreset.name}
                        </span>
                        <span className="cep-template-meta">
                          Consulta base:{" "}
                          {selectedPreset?.queryId != null
                            ? `#${selectedPreset.queryId}`
                            : "não definida"}
                        </span>
                      </div>
                      <div className="cep-template-actions">
                        <button
                          type="button"
                          className="cep-template-action"
                          onClick={(event) => {
                            event.stopPropagation();
                            handlePreviewPreset(selectedPreset);
                          }}
                          disabled={!canEdit}
                          title="Visualizar"
                        >
                          <HiOutlineEye />
                        </button>
                        <button
                          type="button"
                          className="cep-template-action"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleEditPreset(selectedPreset);
                          }}
                          disabled={!canEdit}
                          title="Editar"
                        >
                          <HiPencil />
                        </button>
                        <button
                          type="button"
                          className="cep-template-action cep-template-action--danger"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRequestDeletePreset(selectedPreset);
                          }}
                          disabled={!canEdit}
                          title="Excluir"
                        >
                          <HiTrash />
                        </button>
                      </div>
                    </div>
                  ) : presets.length === 0 ? (
                    <div className="cep-template-empty">
                      Nenhum template disponível ainda.
                    </div>
                  ) : (
                    <div className="cep-template-empty">
                      Selecione um template para continuar.
                    </div>
                  )}

                  {showTemplatePicker && (
                    <div className="cep-template-picker">
                      {presetsLoading && (
                        <div className="cep-template-empty">
                          Carregando templates...
                        </div>
                      )}

                      {!presetsLoading && presets.length === 0 && (
                        <div className="cep-template-empty">
                          Nenhum template disponível.
                        </div>
                      )}

                      {!presetsLoading &&
                        presets.map((preset) => {
                          const rawQueryId =
                            preset?.queryId != null
                              ? preset.queryId
                              : preset?.config?.queryId;
                          return (
                            <div
                              key={preset.id}
                              className={cx(
                                "cep-template-card",
                                String(selectedPresetId) ===
                                  String(preset.id) &&
                                  "cep-template-card--active"
                              )}
                              onClick={() =>
                                handleSelectPreset(String(preset.id))
                              }
                              role="button"
                              tabIndex={0}
                            >
                              <div className="cep-template-main">
                                <span className="cep-template-title">
                                  {preset.name}
                                </span>
                                <span className="cep-template-meta">
                                  Consulta base:{" "}
                                  {rawQueryId != null
                                    ? `#${rawQueryId}`
                                    : "não definida"}
                                </span>
                              </div>
                              <div className="cep-template-actions">
                                <button
                                  type="button"
                                  className="cep-template-action"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handlePreviewPreset(preset);
                                  }}
                                  disabled={!canEdit}
                                  title="Visualizar"
                                >
                                  <HiOutlineEye />
                                </button>
                                <button
                                  type="button"
                                  className="cep-template-action"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleEditPreset(preset);
                                  }}
                                  disabled={!canEdit}
                                  title="Editar"
                                >
                                  <HiPencil />
                                </button>
                                <button
                                  type="button"
                                  className="cep-template-action cep-template-action--danger"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleRequestDeletePreset(preset);
                                  }}
                                  disabled={!canEdit}
                                  title="Excluir"
                                >
                                  <HiTrash />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
                {!canEdit && (
                  <span className="cep-form-helper">
                    Apenas ADMIN ou POWER_USER podem escolher o template.
                  </span>
                )}
                {canEdit && !presetsLoading && presets.length === 0 && (
                  <Button
                    size="sm"
                    className="cep-create-template"
                    onClick={handleOpenSettings}
                  >
                    Criar novo motor
                  </Button>
                )}
              </div>

              <div className="cep-form-block">
                <form onSubmit={handleSimulate} className="cep-form-row">
                  <div className="cep-form-fields">
                    <div className="cep-form-field">
                      <label htmlFor="cpfCnpj" className="cep-form-label">
                        CPF/CNPJ
                      </label>
                      <TextInput
                        id="cpfCnpj"
                        type="text"
                        value={formatCpfCnpj(cpfCnpj)}
                        onChange={handleCpfCnpjChange}
                        color={cpfCnpjError ? "failure" : undefined}
                        helperText={
                          cpfCnpjError ? (
                            <span className="cep-form-error-text">
                              {cpfCnpjError}
                            </span>
                          ) : null
                        }
                        disabled={!canRunSimulation || analysisLoading}
                        className="cep-form-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    color="indigo"
                    className="cep-analyze-button"
                    disabled={
                      !canRunSimulation ||
                      loadingConfig ||
                      analysisLoading ||
                      baseQueryLoading
                    }
                  >
                    <span className="cep-analyze-content">
                      {analysisLoading || loadingConfig || baseQueryLoading ? (
                        <Spinner size={"sm"} />
                      ) : (
                        <HiSearch className="cep-analyze-icon" />
                      )}
                      <span>
                        {analysisLoading
                          ? "Analisando"
                          : loadingConfig
                          ? "Carregando"
                          : baseQueryLoading
                          ? "Carregando"
                          : "Simular"}
                      </span>
                    </span>
                  </Button>
                </form>

                {alert && (
                  <div className="cep-alert-wrapper">
                    <Alert
                      color={
                        alert.type === "success"
                          ? "success"
                          : alert.type === "error"
                          ? "failure"
                          : alert.type === "warning"
                          ? "warning"
                          : "info"
                      }
                      onDismiss={() => setAlert(null)}
                    >
                      <span className="text-xs">{alert.message}</span>
                    </Alert>
                  </div>
                )}
              </div>

              <div className="cep-result-block">
                {canRunSimulation &&
                  (analysisLoading ? (
                    <AnalysisResultSkeleton />
                  ) : analysisResult ? (
                    <AnalysisResultCard
                      result={analysisResult}
                      onOpenDetails={handleOpenDetails}
                    />
                  ) : (
                    <div className="cep-empty-result">
                      <p className="cep-empty-result-title">
                        Resultado da análise com o motor
                      </p>
                      <p className="cep-empty-result-text">
                        Após executar uma consulta, o resultado consolidado do
                        motor (score, risco, regras disparadas e recomendação)
                        será exibido aqui.
                      </p>
                      <p className="cep-empty-result-hint">
                        A integração pode consumir o retorno da API e popular
                        este card com o parecer de crédito gerado
                        automaticamente.
                      </p>
                    </div>
                  ))}
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* MODAL DE DETALHES DAS OBSERVAÇÕES */}
      <Modal show={detailsModal.open} size="lg" onClose={handleCloseDetails}>
        <Modal.Header>{detailsModal.label}</Modal.Header>
        <Modal.Body>
          {/* Exemplo de renderização de protestos / pendências / cheques */}
          {detailsModal.type === "PROTESTOS" && (
            <>
              <p className="mb-2 text-sm text-gray-600">
                Listagem de protestos encontrados para esta consulta.
              </p>
              {Array.isArray(analysisResult?.protestos) &&
              analysisResult.protestos.length ? (
                <div className="overflow-x-auto">
                  <table className="cep-details-table">
                    <thead>
                      <tr>
                        <th>Cartório</th>
                        <th>UF</th>
                        <th>Valor</th>
                        <th>Data</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysisResult.protestos.map((p, idx) => (
                        <tr key={idx}>
                          <td>{p.cartorio || p.cartorioNome || "—"}</td>
                          <td>{p.uf || p.estado || "—"}</td>
                          <td>
                            {typeof p.valor !== "undefined"
                              ? formatMoney(p.valor)
                              : "—"}
                          </td>
                          <td>{p.data || p.dataProtesto || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-xs text-gray-500">
                  Nenhuma listagem detalhada de protestos foi encontrada no
                  retorno.
                </p>
              )}
            </>
          )}

          {detailsModal.type === "PENDENCIAS" && (
            <>
              <p className="mb-2 text-sm text-gray-600">
                Pendências financeiras que influenciaram o parecer do motor.
              </p>
              {Array.isArray(analysisResult?.pendenciasFinanceiras) &&
              analysisResult.pendenciasFinanceiras.length ? (
                <div className="overflow-x-auto">
                  <table className="cep-details-table">
                    <thead>
                      <tr>
                        <th>Credor</th>
                        <th>Origem</th>
                        <th>Valor</th>
                        <th>Situação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysisResult.pendenciasFinanceiras.map((p, idx) => (
                        <tr key={idx}>
                          <td>{p.credor || p.empresa || "—"}</td>
                          <td>{p.origem || p.modalidade || "—"}</td>
                          <td>
                            {typeof p.valor !== "undefined"
                              ? formatMoney(p.valor)
                              : "—"}
                          </td>
                          <td>{p.situacao || p.status || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-xs text-gray-500">
                  Nenhum detalhe de pendências financeiras disponível.
                </p>
              )}
            </>
          )}

          {detailsModal.type === "CHEQUES" && (
            <>
              <p className="mb-2 text-sm text-gray-600">
                Cheques sem fundo considerados na análise.
              </p>
              {Array.isArray(analysisResult?.chequesSemFundo) &&
              analysisResult.chequesSemFundo.length ? (
                <div className="overflow-x-auto">
                  <table className="cep-details-table">
                    <thead>
                      <tr>
                        <th>Banco</th>
                        <th>Agência</th>
                        <th>Número</th>
                        <th>Ocorrência</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysisResult.chequesSemFundo.map((c, idx) => (
                        <tr key={idx}>
                          <td>{c.banco || c.codBanco || "—"}</td>
                          <td>{c.agencia || "—"}</td>
                          <td>{c.numeroCheque || "—"}</td>
                          <td>{c.motivo || c.ocorrencia || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-xs text-gray-500">
                  Nenhum detalhe de cheques sem fundo disponível.
                </p>
              )}
            </>
          )}

          {/* fallback genérico */}
          {["PROTESTOS", "PENDENCIAS", "CHEQUES"].includes(
            detailsModal.type
          ) === false && (
            <p className="text-sm text-gray-600">
              {detailsModal.observation
                ? typeof detailsModal.observation === "string"
                  ? detailsModal.observation
                  : JSON.stringify(detailsModal.observation, null, 2)
                : "Nenhum detalhe adicional disponível para esta observação."}
            </p>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={presetPreview.open}
        size="3xl"
        onClose={() => setPresetPreview({ open: false, preset: null })}
      >
        <Modal.Header className="py-2">
          <span className="text-base mt-1">
            {presetPreview.preset?.name || "Template"}
          </span>
        </Modal.Header>
        <Modal.Body>
          {presetPreview.preset?.config ? (
            renderPresetSummary()
          ) : (
            <p className="text-sm text-gray-600">
              Nenhuma configuração disponível para este template.
            </p>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={Boolean(presetDelete)}
        size="md"
        onClose={() => setPresetDelete(null)}
      >
        <Modal.Header>Excluir template</Modal.Header>
        <Modal.Body>
          <p className="text-sm text-gray-600">
            Tem certeza que deseja excluir o template{" "}
            <strong>{presetDelete?.name}</strong>?
          </p>
          <div className="cep-modal-actions">
            <Button color="light" onClick={() => setPresetDelete(null)}>
              Cancelar
            </Button>
            <Button color="failure" onClick={handleConfirmDeletePreset}>
              Excluir
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
